import React, { useEffect, useRef } from 'react';
import { useBrands } from '../hooks/useBrands';

export const BrandCarousel: React.FC = () => {
  const { brands, loading } = useBrands();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || brands.length === 0) return;

    let scrollPosition = 0;
    const scrollSpeed = 1; // pixels per frame
    const scrollWidth = scrollContainer.scrollWidth / 2; // We duplicate content, so half is the loop point

    const scroll = () => {
      scrollPosition += scrollSpeed;
      
      if (scrollPosition >= scrollWidth) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
    };

    const intervalId = setInterval(scroll, 30); // ~33fps

    return () => clearInterval(intervalId);
  }, [brands]);

  if (loading) {
    return (
      <div className="py-12 bg-white">
        <div className="text-center text-gray-500">Loading brands...</div>
      </div>
    );
  }

  if (brands.length === 0) {
    return null; // Don't show carousel if no brands
  }

  // Duplicate brands array for seamless loop
  const duplicatedBrands = [...brands, ...brands];

  return (
    <div className="py-16 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12">
          Trusted by Leading Brands
        </h2>
        
        <div className="relative">
          {/* Gradient overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling container */}
          <div
            ref={scrollRef}
            className="flex gap-12 overflow-x-hidden scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {duplicatedBrands.map((brand, index) => (
              <div
                key={`${brand.id}-${index}`}
                className="flex-shrink-0 flex items-center justify-center bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100"
                style={{ width: '180px', height: '120px' }}
              >
                <img
                  src={brand.logoUrl}
                  alt={brand.name}
                  className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
        
        <p className="text-center text-gray-600 mt-8">
          Proudly partnering with {brands.length} industry-leading brands
        </p>
      </div>
    </div>
  );
};
